import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { Router, RouterLinkActive, RouterLinkWithHref } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { LucideAngularModule } from 'lucide-angular';
import { SvgIcon } from 'app/shared/svg-icon/svg-icon';
import { SideBarItem } from './sidebar-item';

const PINNED_STORAGE_KEY = 'pc-sidebar-pinned';

/**
 * Mobile breakpoint, in px. Must match the layout's `md` screen (classy uses
 * `isScreenSmall = !aliases.includes('md')`, md = 960px) so the header
 * hamburger, the zeroed content margin and the sidebar's own overlay behaviour
 * all flip at the same width. Below this the sidebar is a full overlay driven by
 * `_mobileOpen`; at or above it, it's the floating hover/pin rail.
 */
const MOBILE_BREAKPOINT = 960;

@Component({
  selector: 'primecontrol-side-bar',
  imports: [
    NgClass,
    RouterLinkWithHref,
    RouterLinkActive,
    TranslateModule,
    LucideAngularModule,
    SvgIcon,
  ],
  templateUrl: './side-bar.html',
  styleUrl: './side-bar.css',
})
export class SideBar {
  public items = input<SideBarItem[]>([]);
  public title = input<string>('MENU');
  public externalOpen = input<boolean | undefined>(undefined);
  public toggle = output<boolean>();
  public pinChange = output<boolean>();
  public expandedChange = output<boolean>();

  /** Who is signed in, for the phone panel's header (desktop keeps the title). */
  public userName = input<string>('');
  public userEmail = input<string>('');
  /** Cerrar sesión, in the phone panel's footer within thumb reach. */
  public signOut = output<void>();

  public userInitials = computed(() =>
    this.userName()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join('')
  );

  private _router = inject(Router);

  /** App version shown in the sidebar footer (mirrors brandbot's version line). */
  public appVersion = signal<string>('v2.3');

  public isPinned = signal<boolean>(false);
  public isHovered = signal<boolean>(false);
  private _mobileOpen = signal<boolean>(false);
  /** Reactive viewport-is-mobile flag (kept in sync via the window:resize host listener). */
  private _isMobile = signal<boolean>(SideBar._computeIsMobile());

  /** Row icon size: 18px for a finger on phones, 16px on the desktop rail. */
  public rowIconSize = computed(() => (this._isMobile() ? 18 : 16));

  /** Whether the sidebar is visually expanded (pinned, hovered, or mobile open) */
  public isExpanded = computed(() => {
    if (this._isMobile()) return this._mobileOpen();
    return this.isPinned() || this.isHovered();
  });

  @HostListener('window:resize')
  public onWindowResize(): void {
    this._isMobile.set(SideBar._computeIsMobile());
  }

  constructor() {
    effect(() => {
      const external = this.externalOpen();
      if (external !== undefined) {
        this._mobileOpen.set(external);
      }
    });

    effect(() => {
      this.expandedChange.emit(this.isExpanded());
    });

    this._loadPinnedState();
  }

  public toggleMenu(): void {
    if (this._isMobile()) {
      const newState = !this._mobileOpen();
      this._mobileOpen.set(newState);
      this.toggle.emit(newState);
    }
  }

  public togglePin(): void {
    const newPinned = !this.isPinned();
    this.isPinned.set(newPinned);
    this._savePinnedState(newPinned);
    this.pinChange.emit(newPinned);
  }

  public onMouseEnter(): void {
    if (!this._isMobile()) {
      this.isHovered.set(true);
    }
  }

  public onMouseLeave(): void {
    if (!this._isMobile()) {
      this.isHovered.set(false);
    }
  }

  public onLinkClick(): void {
    if (this._isMobile()) {
      this._mobileOpen.set(false);
      this.toggle.emit(false);
    }
  }

  public isItemActive(item: SideBarItem): boolean {
    if (!item.ref) return false;

    const currentUrl = this._router.url;
    const refs = item.ref.split(',').map((r) => r.trim());
    return refs.some((ref) => currentUrl.includes(ref));
  }

  public isSpriteIcon(icon: string | undefined): boolean {
    return !!icon && icon.includes(':');
  }

  private static _computeIsMobile(): boolean {
    return typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT;
  }

  private _loadPinnedState(): void {
    if (typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem(PINNED_STORAGE_KEY);
      if (saved === 'true') {
        this.isPinned.set(true);
      }
    }
  }

  private _savePinnedState(pinned: boolean): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(PINNED_STORAGE_KEY, String(pinned));
    }
  }
}
